import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";
import { getCompanyList } from "@endpoints/company.service";
import {
  addJob,
  getMatchJobSkills,
  updateJob,
} from "@endpoints/job.service";
import { setCompanies } from "@store/actions/company.action";
import { JobStatus, JobType, WorkPlaceOptions } from "@utils/constants";
import Detail from "./components/Detail";
import Description from "./components/Description";
import Skills from "./components/Skills";
import Settings from "./components/Settings";
import { colors } from "@utils/theme";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { RecruiterCheckoutPage } from "../Stripe/RecruiterCheckoutPage";
import { isNullOrEmpty } from "@utils/helperFunction";
import Finish from "./components/Finish";

const STEP_CONFIG = [
  { label: "Details", component: Detail },
  { label: "Description", component: Description },
  { label: "Skills", component: Skills },
  { label: "Settings", component: Settings },
  { label: "Finish", component: Finish },
];

const CreateJobPage2 = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { companies } = useSelector((state) => state?.company);

  const [activeStep, setActiveStep] = useState(1);
  const [createNewCompany, setCreateNewCompany] = useState(false);
  const isSubscribed = useSelector((state) => state.auth?.user?.isSubscribed);
  const [showCheckout, setShowCheckout] = useState(false);
  const [currentJobId, setCurrentJobId] = useState("");
  const [isPaid, setIsPaid] = useState(false);

  console.log("this is state", state);

  // Add function to handle back from checkout
  const handleBackFromCheckout = () => {
    setShowCheckout(false);
  };

  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: useInitializeFormDefaults(state, companies),
    mode: "onChange",
  });

  const fields = watch();

  useEffect(() => {
    fetchCompanyList();
  }, [createNewCompany]);

  const fetchTJobSkills = async (jobId) => {
    try {
      const { data, success } = await getMatchJobSkills(jobId);
      if (success && data) {
      }
    } catch (error) {}
  };

  const fetchCompanyList = useCallback(() => {
    getCompanyList()
      .then(({ data, success }) => {
        if (success) dispatch(setCompanies(data));
      })
      .finally(() => setValue("companiesLoading", false));
  }, [dispatch, setValue]);

  const handleNext = useCallback(async () => {
    const isStepValid = await trigger();
    if (!isStepValid) return;

    if (createNewCompany) {
      setCreateNewCompany(false);
      return;
    }

    if (activeStep === 1) {
      setActiveStep(2);
    } else if (activeStep === 2) {
      await createJob(fields);

      setActiveStep(3);
    } else if (activeStep === 3) {
      const jobId = fields?.createdDraftJob?._id;
      if (jobId) {
        await fetchTJobSkills(jobId);
      }
      setActiveStep(4);
    } else if (activeStep === 4) {
      // if (isSubscribed) {
      //   await finishJob();
      // } else {
      //   setShowCheckout(true);
      //   // navigate("/recruiter-checkout");
      // }
      completeStep4();
    } else if (activeStep === 5) {
      if (isPaid) {
        await finishJob();
      } else {
        enqueueSnackbar(
          "You must pay the job posting fee before finishing the job creation.",
          { variant: "error" }
        );
      }
      // if (isSubscribed) {
      //   await finishJob();
      // } else {
      //   setShowCheckout(true);
      //   // navigate("/recruiter-checkout");
      // }
    }
  }, [activeStep, createNewCompany, fields]);

  const createJob = useCallback(
    async (data) => {
      try {
        setValue("isLoading", true);
        const jobTypeValue = JobType.find(
          (x) => x.label?.toLowerCase() === data?.jobType?.toLowerCase()
        )?.value;
        const workPlaceOptionValue = WorkPlaceOptions.find(
          (x) => x.label?.toLowerCase() === data?.workPlaceOption?.toLowerCase()
        )?.value;

        const cleanSalaryMin = data?.salaryRangeFrom
          ? String(data.salaryRangeFrom).replace(/,/g, "")
          : data?.salaryRangeFrom;

        const cleanSalaryMax = data?.salaryRangeTo
          ? String(data.salaryRangeTo).replace(/,/g, "")
          : data?.salaryRangeTo;

        const jobPayload = {
          title: data?.jobTitle,
          description: data?.jobDescription,
          location: data?.jobLocation,
          salary: {
            min: cleanSalaryMin,
            max: cleanSalaryMax,
          },
          industryType: data?.industryType,
          workPlaceOption: workPlaceOptionValue,
          jobType: jobTypeValue,
          companyId: data?.selectedCompany?._id,
          status: JobStatus.DRAFT,
          isSalaryHidden: data?.isSalaryHidden,
          ...(!isNullOrEmpty(data?.jobRequirementId) && {
            jobRequirementId: data?.jobRequirementId,
          }),
        };

        const response =
          data?.createdDraftJob && !isNullOrEmpty(data?.createdDraftJob?._id)
            ? await updateJob({
                jobId: data?.createdDraftJob?._id,
                ...jobPayload,
              })
            : await addJob(jobPayload);

        if (response?.success) {
          setValue("createdDraftJob", response?.data);
          setActiveStep((prev) => prev + 1);
          setCurrentJobId(response.data._id);
        }
        setActiveStep((prev) => prev + 1);
      } catch (error) {
        enqueueSnackbar(error, { variant: "error" });
      } finally {
        setValue("isLoading", false);
      }
    },
    [setValue, enqueueSnackbar]
  );

  const finishJob = useCallback(async () => {
    try {
      setValue("isLoading", true);
      const { success } = await updateJob({
        jobId: fields?.createdDraftJob?._id,
        status: JobStatus.LIVE,
        companyId: fields?.selectedCompany?._id,
        showCompanyDetails: fields?.locationProfile,
        publishedDate: new Date(fields?.publishDate).toISOString(),
        referralAmount: parseInt(fields.referralAmount),
      });

      if (success) navigate("/job", { replace: true });
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setValue("isLoading", false);
    }
  }, [fields, navigate, enqueueSnackbar]);

  const completeStep4 = useCallback(async () => {
    try {
      setValue("isLoading", true);
      const { success } = await updateJob({
        jobId: fields?.createdDraftJob?._id,
        // status: JobStatus.LIVE,
        companyId: fields?.selectedCompany?._id,
        showCompanyDetails: fields?.locationProfile,
        publishedDate: new Date(fields?.publishDate).toISOString(),
        // referralAmount: parseInt(fields.referralAmount),
      });

      if (success) setActiveStep(5);
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setValue("isLoading", false);
    }
  }, [fields, navigate, enqueueSnackbar]);

  const renderStepContent = useMemo(() => {
    const CurrentComponent = STEP_CONFIG[activeStep - 1].component;
    return (
      <CurrentComponent
        control={control}
        errors={errors}
        createdDraftJob={fields?.createdDraftJob}
        setCreateNewCompany={setCreateNewCompany}
        createNewCompany={createNewCompany}
        loading={fields?.companiesLoading}
        setValue={setValue}
        fields={fields}
        onPaymentSuccess={async () => {
          setIsPaid(true);
          await finishJob();
        }}
        currentJobId={currentJobId}
      />
    );
  }, [activeStep, control, errors, fields]);

  return (
    <div className="flex flex-col rounded-lg bg-white w-full flex-grow p-4">
      {showCheckout ? (
        <>
          <BackButton
            navigate={handleBackFromCheckout}
            navigateTo={null}
            label="Back to job posting"
          />
          <RecruiterCheckoutPage
            onPaymentSuccess={async () => {
              await finishJob();
              setShowCheckout(false);
            }}
            currentJobId={currentJobId}
          />
        </>
      ) : (
        <>
          <BackButton
            navigate={navigate}
            navigateTo={-1}
            label="Back to listing"
          />
          <form className="flex flex-col items-center w-full h-full">
            <StepIndicator
              steps={STEP_CONFIG}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
            />

            <div className="flex flex-col py-5 flex-1 overflow-y-scroll no-scrollbar w-full">
              {renderStepContent}
            </div>

            <NavigationButtons
              activeStep={activeStep}
              onNext={handleNext}
              onPrevious={() => setActiveStep((prev) => prev - 1)}
              totalSteps={STEP_CONFIG.length}
              isLoading={fields?.isLoading}
            />
          </form>
        </>
      )}
    </div>
  );
};

const BackButton = ({ navigate, navigateTo, label }) => (
  <button
    onClick={() => (navigateTo ? navigate(navigateTo) : navigate())}
    className="flex items-center cursor-pointer"
  >
    <ChevronLeftIcon className="w-4 h-4" fill="#4d4d4d" />
    <label className="text-xs font-medium text-primary">{label}</label>
  </button>
);

const StepIndicator = ({ steps, activeStep, setActiveStep }) => (
  <div className="flex justify-center items-center my-4 py-6 w-full bg-[#FAFAFA] rounded">
    <div className="flex gap-4 items-center">
      {steps.map((step, stepIndex) => {
        const isActive = stepIndex + 1 === activeStep;
        return (
          <React.Fragment key={step.label}>
            {stepIndex !== 0 && (
              <div className="w-14 h-px border border-b border-light-black" />
            )}
            <div
              // onClick={() => setActiveStep(stepIndex + 1)}
              className={`flex gap-6 ${
                isActive ? "bg-primary" : "bg-white"
              } rounded items-center`}
            >
              <div className="flex items-center gap-2">
                <label
                  className={`font-medium text-xs px-6 py-2 ${
                    isActive ? "text-white" : "text-primary"
                  }`}
                >
                  {step.label}
                </label>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

const NavigationButtons = ({
  activeStep,
  onNext,
  onPrevious,
  totalSteps,
  isLoading = false,
}) => (
  <div className="flex justify-between w-full px-20 py-5">
    <button
      type="button"
      onClick={onPrevious}
      disabled={activeStep === 1 || isLoading}
      className={`bg-[#F7F7F7] rounded-md text-xs px-4 py-2 self-end text-primary
        ${activeStep === 1 ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      Back
    </button>

    <div className="flex gap-5">
      {activeStep !== 1 && (
        <button
          type="button"
          disabled={isLoading}
          className="rounded-md text-xs px-4 py-2 self-end"
        >
          Save draft
        </button>
      )}
      <button
        type="button"
        onClick={onNext}
        disabled={isLoading}
        className="bg-primary flex items-center text-white rounded-md text-xs px-4 py-2 self-end"
      >
        {isLoading && (
          <LoadingAnimation color={colors.White} className="w-4 h-4" />
        )}
        {activeStep === totalSteps ? "Finish" : "Continue"}
      </button>
    </div>
  </div>
);

// Helper function to initialize form defaults
const useInitializeFormDefaults = (state, companies) => ({
  companiesLoading: companies?.length > 0 ? false : true,
  industryType: state?.industryType,
  jobDescription: state?.description,
  workPlaceOption: state
    ? WorkPlaceOptions?.find((x) => state?.workPlaceOption === x.value)?.label
    : undefined,
  jobLocation: state?.location,
  jobTitle: state?.title,
  jobType: state
    ? JobType?.find((x) => state?.jobType === x.value)?.label
    : undefined,
  salaryRangeFrom: state?.salary?.min,
  salaryRangeTo: state?.salary?.max,
  isSalaryHidden: state?.isSalaryHidden ?? false,
  selectedCompany: state
    ? companies?.find((x) => x._id === state?.companyId)
    : undefined,
  createdDraftJob: state ?? undefined,
  publishDate: undefined,
  locationProfile: false,
  referralAmount: state?.referralAmount,
  jobRequirementId: state?.jobRequirementId,
});

export default CreateJobPage2;
